import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '../../core/hooks/useTranslation'
import DeleteModal from '../../components/ui/DeleteModal'
import Modal from '../../components/ui/Modal'
import DateInput from '../../components/ui/DateInput'
import CampaignCard from '../../components/campaign/CampaignCard'
import { campaignService } from '../../core/services/campaign.service'
import { CampaignData } from '../../core/models/campaign'
import { XMarkIcon } from '@heroicons/react/24/outline'
import AddButton from '../../components/buttons/AddButton'
import FilterButton from '../../components/buttons/FilterButton'
import { Megaphone, TrendingUp, TrendingDown } from 'lucide-react'
import toast from 'react-hot-toast'
import { AppRoute } from '../../core/config/app-routes'

export default function CampaignManagementPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()

    // Campaigns state
    const [campaigns, setCampaigns] = useState<CampaignData[]>([])
    const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(false)
    const [deleteCampaignId, setDeleteCampaignId] = useState<number | null>(null)
    const [isDeletingCampaign, setIsDeletingCampaign] = useState(false)
    const [showFilterModal, setShowFilterModal] = useState(false)

    // Ref to prevent duplicate loading
    const campaignsLoadedRef = useRef(false)

    // Filters - Default values (Applied filters)
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')
    const [startDateFilter, setStartDateFilter] = useState<string>('')
    const [endDateFilter, setEndDateFilter] = useState<string>('')

    // Sorting - يمكن اختيار أكثر من معيار ترتيب (Applied sort criteria)
    const [sortCriteria, setSortCriteria] = useState<{
        scans?: 'asc' | 'desc'
        visits?: 'asc' | 'desc'
        orders?: 'asc' | 'desc'
    }>({})

    // Temporary filters for modal (not applied until "Apply" is clicked)
    const [tempStatusFilter, setTempStatusFilter] = useState<'all' | 'active' | 'inactive'>('active')
    const [tempStartDateFilter, setTempStartDateFilter] = useState<string>('')
    const [tempEndDateFilter, setTempEndDateFilter] = useState<string>('')
    const [tempSortCriteria, setTempSortCriteria] = useState<{
        scans?: 'asc' | 'desc'
        visits?: 'asc' | 'desc'
        orders?: 'asc' | 'desc'
    }>({})

    // Load campaigns with filters from backend
    const loadCampaigns = async (customFilters?: {
        status?: 'all' | 'active' | 'inactive'
        startDate?: string
        endDate?: string
        sortBy?: { scans?: 'asc' | 'desc'; visits?: 'asc' | 'desc'; orders?: 'asc' | 'desc' }
    }) => {
        setIsLoadingCampaigns(true)
        try {
            const filters = {
                status: customFilters?.status ?? statusFilter,
                startDate: customFilters?.startDate ?? (startDateFilter || undefined),
                endDate: customFilters?.endDate ?? (endDateFilter || undefined),
                sortBy: customFilters?.sortBy ?? (Object.keys(sortCriteria).length > 0 ? sortCriteria : undefined)
            }

            const data = await campaignService.getCampaigns(filters)
            setCampaigns(data)
        } catch (error: any) {
            console.error('Failed to load campaigns:', error)
            // Error message is already displayed by axios interceptor for HTTP errors
            // Only show fallback if no error response was received (network error, etc.)
            if (!error?.response) {
                toast.error(error.message || t('error') || 'Failed to load campaigns')
            }
        } finally {
            setIsLoadingCampaigns(false)
        }
    }

    // Load campaigns on component mount
    useEffect(() => {
        if (campaignsLoadedRef.current) return

        campaignsLoadedRef.current = true
        loadCampaigns()
    }, [])

    // Copy current filters to temporary filters when modal opens
    useEffect(() => {
        if (showFilterModal) {
            setTempStatusFilter(statusFilter)
            setTempStartDateFilter(startDateFilter)
            setTempEndDateFilter(endDateFilter)
            setTempSortCriteria({ ...sortCriteria })
        }
    }, [showFilterModal])

    // Handle apply filters button
    const handleApplyFilters = async () => {
        // Update applied filters
        setStatusFilter(tempStatusFilter)
        setStartDateFilter(tempStartDateFilter)
        setEndDateFilter(tempEndDateFilter)
        setSortCriteria({ ...tempSortCriteria })

        // Close modal
        setShowFilterModal(false)

        // Load campaigns with new filters
        await loadCampaigns({
            status: tempStatusFilter,
            startDate: tempStartDateFilter,
            endDate: tempEndDateFilter,
            sortBy: Object.keys(tempSortCriteria).length > 0 ? tempSortCriteria : undefined
        })
    }

    const handleDeleteCampaign = async () => {
        if (!deleteCampaignId) return

        setIsDeletingCampaign(true)
        try {
            await campaignService.deleteCampaign(deleteCampaignId)
            toast.success(t('campaignDeleted'))
            await loadCampaigns()
            setDeleteCampaignId(null)
        } catch (error: any) {
            console.error('Failed to delete campaign:', error)
            // Error message is already displayed by axios interceptor for HTTP errors
            // Only show fallback if no error response was received (network error, etc.)
            if (!error?.response) {
                toast.error(error.message || t('error') || 'Failed to delete campaign')
            }
        } finally {
            setIsDeletingCampaign(false)
        }
    }

    // Get active filters and sort as badges
    const getActiveFilters = () => {
        const filters: Array<{ id: string, label: string, type: string }> = []
        if (statusFilter !== 'all') {
            filters.push({
                id: 'status',
                label: statusFilter === 'active' ? t('active') : t('inactive'),
                type: 'status'
            })
        }
        if (startDateFilter) {
            filters.push({
                id: 'startDate',
                label: `${t('startDate')}: ${new Date(startDateFilter).toLocaleDateString('en-US', { calendar: 'gregory' })}`,
                type: 'startDate'
            })
        }
        if (endDateFilter) {
            filters.push({
                id: 'endDate',
                label: `${t('endDate')}: ${new Date(endDateFilter).toLocaleDateString('en-US', { calendar: 'gregory' })}`,
                type: 'endDate'
            })
        }

        // Add sort criteria badges
        if (sortCriteria.scans) {
            filters.push({
                id: 'scans',
                label: sortCriteria.scans === 'desc' ? t('mostScans') : t('leastScans'),
                type: 'sort'
            })
        }
        if (sortCriteria.visits) {
            filters.push({
                id: 'visits',
                label: sortCriteria.visits === 'desc' ? t('mostVisits') : t('leastVisits'),
                type: 'sort'
            })
        }
        if (sortCriteria.orders) {
            filters.push({
                id: 'orders',
                label: sortCriteria.orders === 'desc' ? t('mostOrders') : t('leastOrders'),
                type: 'sort'
            })
        }

        return filters
    }

    // Remove filter by type
    const removeFilter = async (filterId: string, type: string) => {
        let newStatus = statusFilter
        let newStartDate = startDateFilter
        let newEndDate = endDateFilter
        let newSortCriteria = { ...sortCriteria }

        if (type === 'status') {
            newStatus = 'all'
            setStatusFilter('all')
        } else if (type === 'startDate') {
            newStartDate = ''
            setStartDateFilter('')
        } else if (type === 'endDate') {
            newEndDate = ''
            setEndDateFilter('')
        } else if (type === 'sort') {
            delete newSortCriteria[filterId as 'scans' | 'visits' | 'orders']
            setSortCriteria(newSortCriteria)
        }

        // Load campaigns with updated filters
        await loadCampaigns({
            status: newStatus,
            startDate: newStartDate,
            endDate: newEndDate,
            sortBy: Object.keys(newSortCriteria).length > 0 ? newSortCriteria : undefined
        })
    }

    // Toggle sort criteria (for temporary filters in modal)
    const toggleSort = (key: 'scans' | 'visits' | 'orders', order: 'asc' | 'desc') => {
        setTempSortCriteria(prev => {
            const newCriteria = { ...prev }
            if (newCriteria[key] === order) {
                // إذا كان نفس الترتيب، أزل الترتيب
                delete newCriteria[key]
            } else {
                // غير الترتيب
                newCriteria[key] = order
            }
            return newCriteria
        })
    }

    const activeFilters = getActiveFilters()

    return (
        <div className="space-y-4">
            {/* Header */}

            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                    {t('campaignManagementDescription')}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    {/* Active Filters Badges */}
                    {activeFilters.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                            {activeFilters.map((filter) => (
                                <span
                                    key={filter.id}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-lg"
                                >
                                    <span>{filter.label}</span>
                                    <button
                                        onClick={() => removeFilter(filter.id, filter.type)}
                                        className="hover:bg-orange-200 rounded-full p-0.5 transition-colors"
                                        title={t('remove')}
                                    >
                                        <XMarkIcon className="w-3 h-3" />
                                    </button>
                                </span>
                            ))}
                        </div>
                    )}
                    <FilterButton
                        onClick={() => setShowFilterModal(true)}
                        title={t('filter')}
                    />
                    <AddButton
                        onClick={() => navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE + '/create')}
                        title={t('createCampaign')}
                        variant="icon-only"
                    />
                </div>
            </div>


            {/* Campaigns Card */}
            <div className='pb-2'>
                {isLoadingCampaigns ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : campaigns.length === 0 ? (
                    <div className="text-center py-12 flex flex-col items-center justify-center">
                        <Megaphone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">
                            {t('noCampaigns')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {campaigns.map((campaign) => (
                            <CampaignCard
                                key={campaign.id}
                                campaign={campaign}
                                onEdit={(campaign: CampaignData) => navigate(AppRoute.BUSINESS_OWNER_CAMPAIGNS_ROUTE + `/${campaign.id}/edit`)}
                                onDelete={(id: number) => setDeleteCampaignId(id)}
                                showEditButton={true}
                                showDeleteButton={true}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Filter Modal */}
            <Modal
                isOpen={showFilterModal}
                onClose={() => setShowFilterModal(false)}
                title={t('filter')}
                showCancelButton={true}
                cancelButtonText={t('close')}
                actionButtonText={t('apply')}
                onAction={handleApplyFilters}
            >
                <div className="max-h-[70vh] overflow-y-auto">
                    {/* Date Filters */}
                    <div className="pb-4 border-b border-gray-200">
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <DateInput
                                    label={t('startDate')}
                                    selected={tempStartDateFilter ? new Date(tempStartDateFilter) : null}
                                    onChange={(date: Date | null) => {
                                        setTempStartDateFilter(date ? date.toISOString().split('T')[0] : '')
                                    }}
                                    placeholderText={t('startDate')}
                                    isClearable
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    className="text-sm"
                                />
                                <DateInput
                                    label={t('endDate')}
                                    selected={tempEndDateFilter ? new Date(tempEndDateFilter) : null}
                                    onChange={(date: Date | null) => {
                                        setTempEndDateFilter(date ? date.toISOString().split('T')[0] : '')
                                    }}
                                    placeholderText={t('endDate')}
                                    isClearable
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    className="text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="pt-2 pb-2 border-b border-gray-200">
                        <div className="flex items-center justify-between py-2">
                            <span className="text-sm text-gray-700">{t('advertisingCampaigns')}</span>
                            <div className="flex gap-1">
                                <button
                                    onClick={() => setTempStatusFilter('active')}
                                    className={`px-3 py-1 text-xs rounded border ${tempStatusFilter === 'active'
                                        ? 'border-orange-500 text-orange-600 bg-orange-50'
                                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                        }`}
                                >
                                    {t('active')}
                                </button>
                                <button
                                    onClick={() => setTempStatusFilter('inactive')}
                                    className={`px-3 py-1 text-xs rounded border ${tempStatusFilter === 'inactive'
                                        ? 'border-orange-500 text-orange-600 bg-orange-50'
                                        : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                        }`}
                                >
                                    {t('inactive')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Sorting Section */}
                    <div className="pt-4">
                        <div className="text-xs text-gray-500 mb-3 uppercase tracking-wide">
                            {t('sortBy')}
                        </div>

                        <div className="space-y-2">
                            {/* Scans */}
                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-700">{t('scans')}</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => toggleSort('scans', 'desc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.scans === 'desc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingUp className="w-3 h-3" />
                                        {t('mostScans')}
                                    </button>
                                    <button
                                        onClick={() => toggleSort('scans', 'asc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.scans === 'asc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingDown className="w-3 h-3" />
                                        {t('leastScans')}
                                    </button>
                                </div>
                            </div>

                            {/* Visits */}
                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-700">{t('visits')}</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => toggleSort('visits', 'desc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.visits === 'desc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingUp className="w-3 h-3" />
                                        {t('mostVisits')}
                                    </button>
                                    <button
                                        onClick={() => toggleSort('visits', 'asc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.visits === 'asc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingDown className="w-3 h-3" />
                                        {t('leastVisits')}
                                    </button>
                                </div>
                            </div>

                            {/* Orders */}
                            <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-gray-700">{t('orders')}</span>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => toggleSort('orders', 'desc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.orders === 'desc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingUp className="w-3 h-3" />
                                        {t('mostOrders')}
                                    </button>
                                    <button
                                        onClick={() => toggleSort('orders', 'asc')}
                                        className={`px-3 py-1 text-xs rounded border flex items-center gap-1 ${tempSortCriteria.orders === 'asc'
                                            ? 'border-orange-500 text-orange-600 bg-orange-50'
                                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                                            }`}
                                    >
                                        <TrendingDown className="w-3 h-3" />
                                        {t('leastOrders')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Delete Modal */}
            <DeleteModal
                isOpen={deleteCampaignId !== null}
                onClose={() => setDeleteCampaignId(null)}
                onConfirm={handleDeleteCampaign}
                title={t('deleteCampaign')}
                message={t('deleteCampaignDescription')}
                isLoading={isDeletingCampaign}
            />
        </div>
    )
}


